import type { Declaration, GlossaProgram, LValue, Statement } from '../parser/ast.js';

/**
 * Μια ΣΤΑΘΕΡΑ (μόνο ΓΛΩΣΣΑ — η Ψευδογλώσσα δεν έχει καν ΣΤΑΘΕΡΕΣ) δεν
 * αλλάζει τιμή μετά την αρχική δήλωσή της. Αυτός ο έλεγχος τρέχει ΠΡΙΝ την
 * εκτέλεση (σαν συντακτικό σφάλμα, ίδια λογική με `findUndeclaredGlossaReference`)
 * ώστε να πιάνει την παράνομη ανάθεση ΑΚΟΜΑ ΚΙ αν βρίσκεται σε κλάδο/βρόχο
 * που δεν θα εκτελούνταν σε αυτό το συγκεκριμένο τρέξιμο. Δεν ελέγχει
 * ορίσματα ΚΑΛΕΣΕ (το αν μια ΔΙΑΔΙΚΑΣΙΑ όντως ξαναγράφει μια συγκεκριμένη
 * παράμετρο απαιτεί βαθύτερη ανάλυση της ΔΙΚΗΣ ΤΗΣ σώματος — "απόρριψη αντί
 * για μάντεμα"· αυτή η περίπτωση πιάνεται στο runtime, βλ.
 * `Interpreter.requireNotConstant` στο copy-out).
 */
export interface ConstantReassignment {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

function collectConstantNames(declarations: readonly Declaration[]): Set<string> {
  const names = new Set<string>();
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') names.add(decl.name);
  }
  return names;
}

function checkAssignmentTarget(target: LValue, constants: ReadonlySet<string>): ConstantReassignment | null {
  const name = target.kind === 'Identifier' ? target.name : target.array.name;
  return constants.has(name) ? { name, line: target.line, column: target.column } : null;
}

function checkStatement(stmt: Statement, constants: ReadonlySet<string>): ConstantReassignment | null {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return checkAssignmentTarget(stmt.target, constants);
    case 'ReadStatement': {
      for (const target of stmt.targets) {
        const result = checkAssignmentTarget(target, constants);
        if (result !== null) return result;
      }
      return null;
    }
    case 'SwapStatement': {
      const left = checkAssignmentTarget(stmt.left, constants);
      if (left !== null) return left;
      return checkAssignmentTarget(stmt.right, constants);
    }
    case 'ForStatement': {
      if (constants.has(stmt.variable.name)) {
        return { name: stmt.variable.name, line: stmt.variable.line, column: stmt.variable.column };
      }
      return checkStatements(stmt.body, constants);
    }
    case 'IfStatement': {
      const thenResult = checkStatements(stmt.thenBranch, constants);
      if (thenResult !== null) return thenResult;
      for (const branch of stmt.elseIfBranches) {
        const branchResult = checkStatements(branch.body, constants);
        if (branchResult !== null) return branchResult;
      }
      if (stmt.elseBranch !== null) return checkStatements(stmt.elseBranch, constants);
      return null;
    }
    case 'WhileStatement':
      return checkStatements(stmt.body, constants);
    case 'RepeatUntilStatement':
      return checkStatements(stmt.body, constants);
    case 'SwitchStatement': {
      for (const switchCase of stmt.cases) {
        const result = checkStatements(switchCase.body, constants);
        if (result !== null) return result;
      }
      if (stmt.elseBody !== null) return checkStatements(stmt.elseBody, constants);
      return null;
    }
    case 'WriteStatement':
    case 'CallStatement':
      return null;
  }
}

function checkStatements(statements: readonly Statement[], constants: ReadonlySet<string>): ConstantReassignment | null {
  for (const stmt of statements) {
    const result = checkStatement(stmt, constants);
    if (result !== null) return result;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ παράνομη ανάθεση σε ΣΤΑΘΕΡΑ (με σειρά εμφάνισης —
 * κύριο πρόγραμμα πρώτα, μετά κάθε υποπρόγραμμα), ή `null` αν δεν υπάρχει
 * καμία. Κάθε υποπρόγραμμα ελέγχεται με τις ΔΙΚΕΣ ΤΟΥ σταθερές (αυστηρά
 * τοπική εμβέλεια — βλ. `findUndeclaredGlossaReference`).
 */
export function findConstantReassignment(program: GlossaProgram): ConstantReassignment | null {
  const mainConstants = collectConstantNames(program.declarations);
  const mainResult = checkStatements(program.body, mainConstants);
  if (mainResult !== null) return mainResult;

  for (const sub of program.subprograms) {
    const subConstants = collectConstantNames(sub.declarations);
    const subResult = checkStatements(sub.body, subConstants);
    if (subResult !== null) return subResult;
  }
  return null;
}
